import { Letters, StrengthMeter, Variation } from '../interfaces/AuthInputFields';
import { StringMap } from '../../common/interfaces/stringMap';
import { SignUpFields, ResetPasswordFields, UserProfileFields, MfsLoginFields } from '../interfaces/AuthInputFields';
import { Rater } from '../../survey/interfaces/Rater';

type AllSignUpFieldsKeyType = keyof SignUpFields | keyof UserProfileFields | keyof MfsLoginFields | keyof Rater;

/**
 * Method to check if email is a valid email
 * @param email
 */
export const isEmailValid = (email: string): boolean => {
  const pattern1 = new RegExp(
    // eslint-disable-next-line
    /^[\w!#$%&'*+\-/=?\^_`{|}~]+(\.[\w!#$%&'*+\-/=?\^_`{|}~]+)*@((([\-\w]+\.)+[a-zA-Z]+)|(([0-9]{1,3}\.)+[0-9]+))$/,
  );
  const pattern2 = new RegExp(
    // eslint-disable-next-line
    /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i,
  );
  // eslint-disable-next-line
  const pattern3 = new RegExp(/^([a-zA-Z0-9_\.\-\'\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9\-])+$/i);

  return pattern1.test(email) || pattern2.test(email) || pattern3.test(email);
};

/**
 * Method to check if sign up input fields are valid
 * @param name key name
 * @param value key value
 */
export const validateTextField = (name: AllSignUpFieldsKeyType, value: string | boolean): StringMap => {
  let helperText = '';
  if (!value || value === 'false' || (typeof value === 'string' && value.trim() === '')) {
    helperText = `${name}ValidationText`;
  }
  if (name === 'email' && typeof value === 'string' && !isEmailValid(value)) {
    helperText = 'emailValidationText';
  }
  return {
    [`${name}HelperText`]: helperText,
    [name]: `${value}`,
  };
};

/**
 * Method to check if sign up seelct fields are valid
 * @param name key name
 * @param value key value
 */
export const validateDropdownField = (name: AllSignUpFieldsKeyType, value: string): StringMap => {
  let helperText = '';
  if (!value || value === '' || value === '-1') {
    helperText = `${name}ValidationText`;
  }
  return {
    [`${name}HelperText`]: helperText,
    [name]: value,
  };
};

/**
 * Match Password and confirm password
 * @param password
 * @param confirmPassword
 */
export const matchPassword = (password: string, confirmPassword: string): string => {
  if (password !== confirmPassword) {
    return 'yourPasswordsDoNotMatch';
  }
  return '';
};

/**
 * Method to check password strength
 * @param password
 */
export const getPasswordStrength = (password: string): StrengthMeter => {
  // password cannot be undefined
  let score = 0;
  const charCount: number = password.length;
  // award every unique letter until 5 repetitions
  const letters: Letters = {};
  for (let i = 0; i < password.length; i++) {
    letters[password[i]] = (letters[password[i]] || 0) + 1;
    score += 5.0 / letters[password[i]];
  }

  // bonus points for mixing it up
  const variations: Variation = {
    digits: /\d/.test(password),
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    nonWords: /\W/.test(password),
    moreThanTwoConsecutiveCharacters: !/([a-z\d])\1\1/i.test(password),
  };

  let variationCount = 0;
  let check: keyof Variation;
  for (check in variations) {
    variationCount += variations[check] === true ? 1 : 0;
  }
  score += (variationCount - 1) * 6;
  /**
   * custom logic to change scores
   */
  // if any one condition fails
  // if (!variations.digits || !variations.lower || !variations.upper || charCount <= 8 || charCount >= 50) {
  //   score = 50;
  // }
  // if all conditions pass
  if (!variations.moreThanTwoConsecutiveCharacters && score > 70) {
    score = 70;
  }
  if (
    variations.digits &&
    variations.lower &&
    variations.upper &&
    charCount >= 8 &&
    charCount < 50 &&
    variations.moreThanTwoConsecutiveCharacters
  ) {
    score = 100;
  }

  // score should be max of 100
  if (score > 100) {
    score = 100;
  }

  return {
    password,
    score,
    variations,
    charCount,
  };
};

/**
 * Create password strength meter msgs
 * @param passwordDetails
 * @param email
 */
export const getPasswordStrengthMessageList = (passwordDetails: StrengthMeter, email?: string): Array<string> => {
  // used message translation code --- supporting multiple locales
  const upperCaseMsg = 'upperCaseMsg';
  const lowerCaseMsg = 'lowerCaseMsg';
  const numberMsg = 'numberMsg';
  const minCharacterMsg = 'minCharacterMsg';
  const maxCharacterMsg = 'maxCharacterMsg';
  const moreThanTwoConsecutiveCharactersMsg = 'moreThanTwoConsecutiveCharactersMsg';
  const emailSameAsPasswordMsg = 'emailSameAsPasswordMsg';
  const { variations, charCount, password } = passwordDetails;
  // create password strings to be shown
  const passwordStrings: Array<string> = [];
  if (!variations.upper) {
    passwordStrings.push(upperCaseMsg);
  }
  if (!variations.lower) {
    passwordStrings.push(lowerCaseMsg);
  }
  if (!variations.digits) {
    passwordStrings.push(numberMsg);
  }
  if (charCount < 8) {
    passwordStrings.push(minCharacterMsg);
  }
  if (charCount > 49) {
    passwordStrings.push(maxCharacterMsg);
  }
  if (!variations.moreThanTwoConsecutiveCharacters) {
    passwordStrings.push(moreThanTwoConsecutiveCharactersMsg);
  }
  if (email && password === email) {
    passwordStrings.push(emailSameAsPasswordMsg);
  }
  return passwordStrings;
};

/**
 * User Details input validation handler
 * @param name
 * @param value
 * @param checked
 * @param state
 */
export const validateInputChangeSignUpField = (
  name: string,
  value: string,
  checked?: boolean,
  state?: SignUpFields | ResetPasswordFields,
): { [name: string]: string | number } => {
  let validation: { [name: string]: string | number } = {};
  let passwordStrength: StrengthMeter;

  switch (name) {
    case 'firstName':
    case 'lastName':
    case 'email':
      validation = validateTextField(name, value);
      break;
    case 'gender':
    case 'title':
    case 'countryRegion':
      validation = validateDropdownField(name, value);
      break;
    case 'password':
      validation = validateTextField(name, value);
      if (
        state !== undefined &&
        state.confirmPassword !== '' &&
        validation.value !== '' &&
        validation.passwordHelperText === ''
      ) {
        validation.confirmPasswordHelperText = matchPassword(value, state.confirmPassword);
      }
      if (validation.passwordHelperText === '') {
        passwordStrength = getPasswordStrength(value);
        validation.strength = passwordStrength.score;
      } else {
        validation.strength = 0; // done in case there is an error but still the percentage bar shows
      }
      break;
    case 'confirmPassword':
      validation = validateTextField(name, value);
      if (state !== undefined && validation.value !== '' && validation.confirmPasswordHelperText === '') {
        validation.confirmPasswordHelperText = matchPassword(state.password, value);
      }
      break;
    case 'privacyPolicy':
      if (checked !== undefined) {
        validation = validateTextField(name, checked);
      }
      break;
  }
  return validation;
};

/**
 * User Details input validation handler for Mfs Sign Up fields
 * @param name
 * @param value
 * @param state
 */
export const validateInputChangeMfsSignUpField = (
  name: string,
  value: string,
  state?: MfsLoginFields,
): { [name: string]: string | number } => {
  let validation: { [name: string]: string | number } = {};
  let passwordStrength: StrengthMeter;

  switch (name) {
    case 'name':
    case 'username':
    case 'email':
      validation = validateTextField(name, value);
      break;
    case 'language':
      validation = validateDropdownField(name, value);
      break;
    case 'password':
      validation = validateTextField(name, value);
      if (
        state !== undefined &&
        state.confirmPassword !== '' &&
        validation.value !== '' &&
        validation.passwordHelperText === ''
      ) {
        validation.confirmPasswordHelperText = matchPassword(value, state.confirmPassword);
      }
      if (validation.passwordHelperText === '') {
        passwordStrength = getPasswordStrength(value);
        validation.strength = passwordStrength.score;
      } else {
        validation.strength = 0; // done in case there is an error but still the percentage bar shows
      }
      break;
    case 'newPassword':
      validation = validateTextField(name, value);
      if (
        state !== undefined &&
        state.confirmNewPassword !== '' &&
        validation.value !== '' &&
        validation.newPasswordHelperText === ''
      ) {
        validation.confirmNewPasswordHelperText = matchPassword(value, state.confirmNewPassword);
      }
      if (validation.newPasswordHelperText === '') {
        passwordStrength = getPasswordStrength(value);
        validation.strength = passwordStrength.score;
      } else {
        validation.strength = 0; // done in case there is an error but still the percentage bar shows
      }
      break;
    case 'confirmPassword':
      validation = validateTextField(name, value);
      if (state !== undefined && validation.value !== '' && validation.confirmPasswordHelperText === '') {
        validation.confirmPasswordHelperText = matchPassword(state.password, value);
      }
      break;
    case 'confirmNewPassword':
      validation = validateTextField(name, value);
      if (state !== undefined && validation.value !== '' && validation.confirmNewPasswordHelperText === '') {
        validation.confirmNewPasswordHelperText = matchPassword(state.newPassword, value);
      }
      break;
  }
  return validation;
};
